import { ComponentPropsWithoutRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FieldPath, useFieldArray, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Separator as SeparatorPrimitive } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faFacebook, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import {
    IconDefinition,
    faArrowRight,
    faBuilding,
    faCog,
    faLink,
    faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContact } from 'api/contacts/single';
import { ContactFormArrayFieldActions } from 'assets/js/pages/contacts/ContactFormArrayFieldActions';
import { UseFormContactTags } from 'assets/js/pages/contacts/UseFormContactTags';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import { AddressForm, AddressFormProps } from 'components/address-form/AddressForm';
import { transformServerAddressIntoFormAddress } from 'components/address-form/useAccountAddressForm';
import * as CustomFieldResponseForm from 'components/custom-fields/CustomFieldResponseForm';
import * as Form from 'components/form';
import { SelectItemWithLabel, SelectListItem, UseFormSearchableSelect } from 'components/select';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentPossibleContact } from 'customHooks/useCurrentContact';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteContacts } from 'customHooks/useInfiniteContacts';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { first } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact, ContactEmail, ContactPhone, CustomField } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ContactFormContainerProps extends ComponentPropsWithoutRef<'div'> {}

interface ContactFormInputProps extends ComponentPropsWithoutRef<'input'> {}

interface ContactNameInputProps extends ContactFormInputProps {
    hideAffix?: boolean;
}

const ContactFormContainer = (props: ContactFormContainerProps) => {
    const { className = '', ...rest } = props;

    return <div className={`flex flex-col gap-y-6 ${className}`} {...rest} />;
};

const ContactSocialsForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <>
            <Form.Container>
                <Form.Label name={path('websiteUrl')}>Website URL</Form.Label>
                <Form.TextInputRoot control={control} name={'websiteUrl'}>
                    <Form.TextInputForm />
                    <FontAwesomeIcon icon={faLink} />
                </Form.TextInputRoot>
                <Form.Error name={path('websiteUrl')} />
            </Form.Container>
            <Form.Container>
                <Form.Label name={path('linkedInUrl')}>LinkedIn</Form.Label>
                <Form.TextInputRoot control={control} name={'linkedInUrl'}>
                    <Form.TextInputForm />
                    <FontAwesomeIcon icon={faLinkedin as IconDefinition} />
                </Form.TextInputRoot>
                <Form.Error name={path('linkedInUrl')} />
            </Form.Container>
            <Form.Container>
                <Form.Label className="text-lg font-bold" title="Twitter" name={path('twitterUrl')}>
                    𝕏
                </Form.Label>
                <Form.TextInputRoot control={control} name={'twitterUrl'}>
                    <Form.TextInputForm />
                    <FontAwesomeIcon title="Twitter" icon={faXTwitter as IconDefinition} />
                </Form.TextInputRoot>
                <Form.Error name={path('twitterUrl')} />
            </Form.Container>
            <Form.Container>
                <Form.Label name={path('facebookUrl')}>Facebook</Form.Label>
                <Form.TextInputRoot control={control} name={'facebookUrl'}>
                    <Form.TextInputForm />
                    <FontAwesomeIcon icon={faFacebook as IconDefinition} />
                </Form.TextInputRoot>
                <Form.Error name={path('facebookUrl')} />
            </Form.Container>
        </>
    );
};

const ContactFirstNameForm = (props: ContactNameInputProps) => {
    const { disabled = false, hideAffix = false, required = true } = props;
    const { control, path, register, watch, setValue } = Form.useFormContext<ContactFormValues>();
    const showPrefix = watch('ui.showPrefix');
    const { contactsWithoutName = false } = useFlags();
    const isRequired = required || !contactsWithoutName;

    return (
        <Form.Container>
            <input type={'hidden'} {...register('ui.showPrefix')} />
            <Form.Label name={path('firstName')}>
                First name
                {isRequired && <Form.LabelAsterisk />}
            </Form.Label>
            <div className={'flex gap-2'}>
                {showPrefix && !disabled && (
                    <Form.TextInputRoot
                        control={control}
                        name="firstNamePrefix"
                        inputRootProps={{
                            className: 'w-20',
                        }}
                    >
                        <Form.TextInputForm placeholder="Prefix" readOnly={disabled} />
                    </Form.TextInputRoot>
                )}
                <Form.TextInputRoot
                    control={control}
                    name={'firstName'}
                    rules={isRequired ? { required: 'First name is required' } : {}}
                    inputRootProps={{
                        className: 'flex-grow',
                    }}
                >
                    <Form.TextInputForm placeholder={'First name'} readOnly={disabled} />
                </Form.TextInputRoot>
            </div>
            <Form.Error name={path('firstName')} />
            {!disabled && !hideAffix ? (
                <Button
                    variant="none"
                    size="sm"
                    color="primary"
                    className="font-medium"
                    onClick={() => setValue('ui.showPrefix', !showPrefix)}
                >
                    {showPrefix ? '- Remove Prefix' : '+ Add Prefix'}
                </Button>
            ) : null}
        </Form.Container>
    );
};

const ContactMiddleNameForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('middleName')}>Middle Name</Form.Label>
            <Form.TextInputRoot control={control} name={'middleName'}>
                <Form.TextInputForm placeholder={'Middle name'} />
            </Form.TextInputRoot>
            <Form.Error name={path('middleName')} />
        </Form.Container>
    );
};

const ContactLastNameForm = (props: ContactNameInputProps) => {
    const { disabled = false, hideAffix = false, required = true } = props;
    const { control, register, path, watch, setValue } = Form.useFormContext<ContactFormValues>();
    const showSuffix = watch('ui.showSuffix');
    const selectDisabled = disabled;
    const { contactsWithoutName = false } = useFlags();
    const isRequired = required || !contactsWithoutName;

    return (
        <Form.Container>
            <input type={'hidden'} {...register('ui.showSuffix')} />
            <Form.Label name={path('lastName')}>
                Last name
                {isRequired && <Form.LabelAsterisk />}
            </Form.Label>
            <div className={'flex gap-2'}>
                <Form.TextInputRoot
                    control={control}
                    name={'lastName'}
                    rules={isRequired ? { required: 'Last name is required' } : {}}
                    inputRootProps={{
                        className: 'flex-grow',
                    }}
                >
                    <Form.TextInputForm placeholder={'Last name'} readOnly={disabled} />
                </Form.TextInputRoot>
                {!!showSuffix && !selectDisabled && (
                    <Form.TextInputRoot
                        control={control}
                        name="lastNameSuffix"
                        inputRootProps={{
                            className: 'w-20',
                        }}
                    >
                        <Form.TextInputForm placeholder="Suffix" readOnly={disabled} />
                    </Form.TextInputRoot>
                )}
            </div>
            <Form.Error name={path('lastName')} />
            {!selectDisabled && !hideAffix ? (
                <Button
                    variant="none"
                    size="sm"
                    color="primary"
                    className="font-medium"
                    onClick={() => setValue('ui.showSuffix', !showSuffix)}
                >
                    {showSuffix ? '- Remove Suffix' : '+ Add Suffix'}
                </Button>
            ) : null}
        </Form.Container>
    );
};

const ContactDobForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();
    const { allowDobWithoutYear = false } = useFlags();
    const placeholder = allowDobWithoutYear ? 'MM/DD or MM/DD/YYYY' : 'MM/DD/YYYY';

    return (
        <Form.Container>
            <Form.Label name={path('dob')}>Birthdate</Form.Label>
            <Form.TextInputRoot
                control={control}
                name={'dob'}
                inputRootProps={{
                    mask: 'date',
                }}
            >
                <Form.TextInputForm placeholder={placeholder} />
            </Form.TextInputRoot>
            <Form.Error name={path('dob')} />
        </Form.Container>
    );
};

const ContactGenderForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('gender')}>Gender</Form.Label>
            <Form.SingleSelect control={control} name="gender">
                <SingleSelect.Trigger aria-label="Gender" />
                <SingleSelect.Content>
                    <SingleSelect.Item value="select">
                        <SingleSelect.ItemText>Select</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="male">
                        <SingleSelect.ItemText>Male</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="female">
                        <SingleSelect.ItemText>Female</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="other">
                        <SingleSelect.ItemText>Other</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </Form.SingleSelect>
        </Form.Container>
    );
};

const ContactEmployerForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('employer')}>Employer</Form.Label>
            <Form.TextInputRoot control={control} name={'employer'}>
                <Form.TextInputForm placeholder={'Employer'} />
            </Form.TextInputRoot>
            <Form.Error name={path('employer')} />
        </Form.Container>
    );
};

const ContactCompanyForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('employer')}>Company</Form.Label>
            <Form.TextInputRoot control={control} name={'employer'}>
                <Form.TextInputForm placeholder={'Company'} />
            </Form.TextInputRoot>
            <Form.Error name={path('employer')} />
        </Form.Container>
    );
};

const ContactTitleForm = ({ name = 'legacyTitle' }: { name?: FieldPath<ContactFormValues> }) => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path(name)}>Title</Form.Label>
            <Form.TextInputRoot control={control} name={name}>
                <Form.TextInputForm placeholder={'Title'} />
            </Form.TextInputRoot>
            <Form.Error name={path(name)} />
        </Form.Container>
    );
};

interface ContactEmailsFormProps extends ContactFormInputProps {
    showActions?: boolean;
    isRequired?: boolean;
    name?: 'emails' | 'pointOfContactEmails' | 'companyEmails';
}
const ContactEmailsForm = (props: ContactEmailsFormProps) => {
    const { showActions = false, disabled = false, isRequired = false, name = 'emails' } = props;
    const { control, path } = Form.useFormContext<ContactFormValues>();
    const emailFieldArray = useFieldArray({
        name,
        control,
    });
    const { fields: emailFields } = emailFieldArray;

    return (
        <>
            {emailFields.map((field, index) => (
                <div key={field.id} className={'flex flex-col gap-3'}>
                    <Form.Container>
                        <Form.Label name={path(`${name}.${index}.value`)}>
                            Email
                            {isRequired && <Form.LabelAsterisk />}
                        </Form.Label>
                        <div className={'flex items-center gap-3'}>
                            <Form.TextInputRoot
                                control={control}
                                name={`${name}.${index}.value`}
                                rules={{ required: isRequired && 'Email is required' }}
                                inputRootProps={{
                                    className: 'flex-grow',
                                }}
                            >
                                <Form.TextInputForm placeholder={'Email'} readOnly={disabled} />
                            </Form.TextInputRoot>
                            <Form.SingleSelect control={control} name={`${name}.${index}.type`}>
                                <SingleSelect.Trigger
                                    aria-label="Email Type"
                                    disabled={disabled}
                                    className="basis-min-content"
                                />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="personal">
                                        <SingleSelect.ItemText>Personal</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="work">
                                        <SingleSelect.ItemText>Work</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </div>
                        <Form.Error name={path(`${name}.${index}.value`)} />
                    </Form.Container>
                    {showActions && !disabled ? (
                        <ContactFormArrayFieldActions
                            index={index}
                            label={'email'}
                            path={name}
                            appendValue={{
                                value: '',
                                isPrimary: false,
                                type: 'personal',
                            }}
                            {...emailFieldArray}
                        />
                    ) : null}
                </div>
            ))}
        </>
    );
};

interface ContactPhonesFormProps extends ContactFormInputProps {
    showActions?: boolean;
    name?: 'phones' | 'pointOfContactPhones' | 'companyPhones';
}

const ContactPhonesForm = (props: ContactPhonesFormProps) => {
    const { showActions = false, disabled = false, name = 'phones' } = props;
    const { control, path } = Form.useFormContext<ContactFormValues>();
    const phoneFieldArray = useFieldArray({
        name,
        control,
    });
    const { fields: phoneFields } = phoneFieldArray;

    return (
        <>
            {phoneFields.map((field, index) => (
                <div key={field.id} className={'flex flex-col gap-3'}>
                    <Form.Container>
                        <Form.Label name={path(`${name}.${index}.value`)}>Phone number</Form.Label>
                        <div className={'flex items-center gap-3'}>
                            <Form.TextInputRoot
                                control={control}
                                name={`${name}.${index}.value`}
                                inputRootProps={{
                                    mask: 'phone',
                                    className: 'flex-grow',
                                }}
                            >
                                <Form.TextInputForm
                                    placeholder={'+1 000 000 - 0000'}
                                    readOnly={disabled}
                                />
                            </Form.TextInputRoot>
                            <Form.SingleSelect control={control} name={`${name}.${index}.type`}>
                                <SingleSelect.Trigger
                                    aria-label="Phone Type"
                                    disabled={disabled}
                                    className="basis-min-content"
                                />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="home">
                                        <SingleSelect.ItemText>Home</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="work">
                                        <SingleSelect.ItemText>Work</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="cell">
                                        <SingleSelect.ItemText>Cell</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </div>
                        <Form.Error name={path(`${name}.${index}.value`)} />
                    </Form.Container>
                    {showActions && !disabled ? (
                        <ContactFormArrayFieldActions
                            index={index}
                            label={'phone number'}
                            path={name}
                            appendValue={{
                                value: '',
                                isPrimary: false,
                                type: 'home',
                            }}
                            {...phoneFieldArray}
                        />
                    ) : null}
                </div>
            ))}
        </>
    );
};

interface ContactAddressesFormProps extends AddressFormProps {}

const ContactAddressesForm = (props: ContactAddressesFormProps) => {
    const { control } = Form.useFormContext<ContactFormValues>();
    const addressFieldArray = useFieldArray({
        name: 'addresses',
        control,
    });
    const { fields: addressFields } = addressFieldArray;

    return (
        <>
            {addressFields.map((field, index) => (
                <AddressForm key={field.id} pathPrefix={`addresses.${index}`} {...props} />
            ))}
        </>
    );
};

const ContactNotesForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('note')}>Note</Form.Label>
            <Form.Textarea
                className={'!p-4'}
                control={control}
                name={'note'}
                placeholder={`Met them at last week's event...`}
            />
        </Form.Container>
    );
};

const ContactTagsForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path('tags')}>Tags</Form.Label>
            <UseFormContactTags name={'tags'} control={control} />
        </Form.Container>
    );
};

const ContactEngageForm = () => {
    const { control, path } = Form.useFormContext<ContactFormValues>();
    const [allowEngageEmail, allowEngageSms, allowEngageAddress, type] = useWatch({
        control,
        name: ['allowEngageEmail', 'allowEngageSms', 'allowEngageAddress', 'type'],
    });

    return (
        <>
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-y-2'}>
                    <p className={'text-base font-medium'}>Engage Subscriptions</p>
                    <p>
                        These subscription settings are for{' '}
                        <span className={'font-medium'}>Engage</span> messages only, and will not
                        affect system messages such as email receipts.
                    </p>
                </div>
            </div>
            <div className={'flex items-center justify-between'}>
                <Form.Label name={path('allowEngageEmail')}>Email</Form.Label>
                <Form.Switch control={control} name={'allowEngageEmail'} />
            </div>
            <div className={'flex items-center justify-between'}>
                <Form.Label name={path('allowEngageSms')}>SMS</Form.Label>
                <Form.Switch control={control} name={'allowEngageSms'} />
            </div>
            <div className={'flex items-center justify-between'}>
                <Form.Label name={path('allowEngageAddress')}>Mail</Form.Label>
                <Form.Switch control={control} name={'allowEngageAddress'} />
            </div>
            <Label className={'flex items-center gap-2'}>
                <div>
                    <Form.UseFormCheckboxInput
                        control={control}
                        name={'didConfirmEngage'}
                        rules={{
                            validate: value => {
                                if (
                                    !value &&
                                    (allowEngageSms || allowEngageEmail || allowEngageAddress)
                                ) {
                                    return 'Please confirm you have attained permission from this contact for Engage outreach';
                                }

                                return true;
                            },
                        }}
                    />
                </div>
                <p className={'font-normal italic'}>
                    I confirm this {type === ContactType.Company ? 'organization' : 'person'} gave
                    me permission to notify them via email, SMS, or mail.
                </p>
            </Label>
            <Form.Error name={path('didConfirmEngage')} />
        </>
    );
};

interface ContactSelectFormType
    extends Pick<ContactFormValues, 'companyName' | 'firstName' | 'lastName' | 'addresses'> {
    emails: ContactEmail[];
    phones: ContactPhone[];
    selectedContact: SelectItemWithLabel<Contact>[];
    enableManualBidderNumber?: boolean;
    manualBidderNumber?: number;
}

const ContactSelectItem = ({ contact }: { contact: Contact }) => {
    return (
        <div className={'flex flex-col gap-2'}>
            <p className="font-medium">
                {contact.type === ContactType.Company ? (
                    <span className="flex items-center gap-1">
                        {contact.company_name ?? <span className="text-gray-400">N/A</span>}
                        <Badge size="sm" color="primary">
                            <FontAwesomeIcon icon={faBuilding} /> Company
                        </Badge>
                    </span>
                ) : (
                    contact.name_display
                )}
            </p>
            {contact.primary_email && (
                <p className="font-thin text-gray-600">{`${contact.primary_email}`}</p>
            )}
        </div>
    );
};

interface ContactSelectFormProps {
    disabled?: boolean;
    contactType?: ContactType;
    nameRequired?: boolean;
}
const ContactSelectForm = ({ disabled, contactType, nameRequired }: ContactSelectFormProps) => {
    const { control, setValue, getValues, resetField, watch } =
        Form.useFormContext<ContactSelectFormType>();
    const [searchContacts, setSearchContacts] = useState('');
    const { data: currentContact } = useCurrentPossibleContact();
    const debouncedSearchContacts = useDebounce(searchContacts);
    const { data: contactsData, ref: contactsRef } = useInfiniteContacts(
        {
            query: debouncedSearchContacts,
            type: contactType,
            nameRequired: nameRequired,
        },
        { enabled: !currentContact },
    );
    const { selectContact } = useSelectContact({
        onSuccess: contact => {
            const contactEmails = contact.emails.filter(
                email => email.value === contact.primary_email,
            );
            const contactAddresses = contact.addresses?.[0] ?? {};
            const address = contact.primary_address ?? contactAddresses;

            unstable_batchedUpdates(() => {
                setValue('selectedContact', [
                    {
                        ...getValues('selectedContact')[0],
                        value: contact,
                    },
                ]);
                setValue('companyName', contact.company_name);
                setValue('firstName', contact.first_name);
                setValue('lastName', contact.last_name);
                setValue('emails', contactEmails.length ? contactEmails : getValues('emails'));
                setValue('phones', contact.phones);
                setValue('addresses', [
                    {
                        ...transformServerAddressIntoFormAddress(address),
                        country: address.country ?? '',
                    },
                ]);
            });
        },
    });

    const contacts = contactsData?.pages ?? [];
    const selectedContact = watch('selectedContact');

    const resetBidderConfigs = () => {
        resetField('enableManualBidderNumber');
        resetField('manualBidderNumber');
    };

    const resetContact = () => {
        resetField('firstName');
        resetField('lastName');
        resetField('emails');
        resetField('phones');
        resetField('addresses');
        resetBidderConfigs();
    };

    return (
        <UseFormSearchableSelect
            control={control}
            name={'selectedContact'}
            localSearch={false}
            clearable={true}
            rules={{
                required: 'Select a contact or create a new one',
            }}
            inputProps={{
                placeholder: 'Select existing contact or create new',
                onChange: e => {
                    setSearchContacts(e.currentTarget.value);
                },
                readOnly: first(selectedContact)?.id === NewContactId,
            }}
            onSelectItem={value => {
                if (!value) {
                    setSearchContacts('');
                    return;
                }

                if (value.id) {
                    if (value.id === NewContactId) {
                        resetContact();
                    } else {
                        resetBidderConfigs();
                        selectContact(parseInt(value.id));
                    }
                }
            }}
            disabled={disabled}
        >
            <SelectListItem
                key={NewContactId}
                value={{
                    id: NewContactId,
                    label: `Creating new contact...`,
                }}
            >
                <div className={'flex gap-2'}>
                    <FontAwesomeIcon icon={faPlus} />
                    <p>Create new contact</p>
                </div>
            </SelectListItem>
            {contacts.map((item: Contact) => (
                <SelectListItem
                    key={item.id}
                    value={{
                        id: item.id.toString(),
                        label: getSelectedContactLabel(item),
                        value: item,
                    }}
                >
                    <ContactSelectItem contact={item} />
                </SelectListItem>
            ))}
            <div ref={contactsRef} />
        </UseFormSearchableSelect>
    );
};

const filterFieldsByType = (fields: CustomField[], type: ContactType): CustomField[] => {
    return fields.filter(field => {
        if (type === 'individual') {
            return field.metadata?.show_create;
        }
        if (type === 'company') {
            return field.metadata?.show_create_company;
        }
        return false;
    });
};

const ContactFormCustomFields = ({
    onAddContactFile,
    type = ContactType.Individual,
}: {
    onAddContactFile?: (isAdding: boolean) => void;
    type?: ContactType;
}) => {
    const { accountId, customFields } = useCurrentAccount();
    const fields = filterFieldsByType(customFields?.contact ?? [], type);

    return (
        <>
            <div className={'flex justify-between gap-4'}>
                <p className={'text-base font-medium'}>Custom Fields</p>
                <Link
                    to={`/accounts/${accountId}/contacts/custom-fields`}
                    className={'flex items-center gap-x-1.5 font-medium'}
                >
                    <FontAwesomeIcon icon={faCog} className={'text-gray-600'} />
                    <span className={'text-gray-700'}>Manage</span>
                </Link>
            </div>
            {fields.length === 0 ? (
                <div className={'w-full rounded-lg bg-gray-50 p-4 text-center font-medium'}>
                    <p>
                        <span className={'text-gray-600'}>
                            You haven't created any custom fields yet.
                        </span>{' '}
                        <Link to={`/accounts/${accountId}/contacts/custom-fields`}>
                            Add a field <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    </p>
                </div>
            ) : (
                fields.map(field => (
                    <CustomFieldResponseForm.Root
                        key={field.id}
                        field={field}
                        onAddFile={onAddContactFile}
                    >
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col">
                                <CustomFieldResponseForm.Title />
                                <CustomFieldResponseForm.Description />
                            </div>

                            <div className="flex flex-col">
                                <CustomFieldResponseForm.Input />
                                <CustomFieldResponseForm.FormError />
                            </div>
                        </div>
                    </CustomFieldResponseForm.Root>
                ))
            )}
        </>
    );
};

const ContactFormSeparator = () => <SeparatorPrimitive className={`-mx-8 !w-auto`} />;

interface UseSelectContactProps {
    onSuccess?: (contact: Contact) => void;
}

const useSelectContact = ({ onSuccess }: UseSelectContactProps = {}) => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { error } = useToast();

    const { mutate: selectContact, isPending: isLoadingContact } = useMutation({
        mutationFn: (contactId: number) => {
            const data = queryClient.getQueryData<Contact>(
                accountQueryKeys.contactById(accountId, contactId),
            );

            if (data) {
                return Promise.resolve(data);
            }

            return fetchContact(accountId, contactId);
        },
        onSuccess: contact => {
            queryClient.setQueryData(accountQueryKeys.contactById(accountId, contact.id), contact);
            onSuccess?.(contact);
        },
        onError: (err: GivebutterAxiosError) => {
            error(err.response?.data?.message ?? 'An unknown error occurred');
        },
    });

    return { selectContact, isLoadingContact };
};

const getSelectedContactLabel = (contact: Contact) => {
    if (contact.primary_email) {
        return `${contact.name_display} - ${contact.primary_email}`;
    }

    return contact.name_display;
};

const NewContactId = '-1';

const Container = ContactFormContainer;
const FirstName = ContactFirstNameForm;
const LastName = ContactLastNameForm;
const MiddleName = ContactMiddleNameForm;
const Gender = ContactGenderForm;
const Dob = ContactDobForm;
const Title = ContactTitleForm;
const Socials = ContactSocialsForm;
const Engage = ContactEngageForm;
const Tags = ContactTagsForm;
const Notes = ContactNotesForm;
const Phones = ContactPhonesForm;
const Emails = ContactEmailsForm;
const Addresses = ContactAddressesForm;
const SelectContact = ContactSelectForm;
const CustomFields = ContactFormCustomFields;
const Separator = ContactFormSeparator;
const Employer = ContactEmployerForm;
const Company = ContactCompanyForm;

export {
    NewContactId,
    useSelectContact,
    getSelectedContactLabel,
    filterFieldsByType,
    //
    Container,
    FirstName,
    LastName,
    MiddleName,
    Gender,
    Dob,
    Title,
    Socials,
    Engage,
    Tags,
    Notes,
    Phones,
    Emails,
    Addresses,
    SelectContact,
    CustomFields,
    Separator,
    Employer,
    Company,
    //
    ContactFormContainer,
    ContactFirstNameForm,
    ContactLastNameForm,
    ContactMiddleNameForm,
    ContactGenderForm,
    ContactDobForm,
    ContactTitleForm,
    ContactSocialsForm,
    ContactEngageForm,
    ContactTagsForm,
    ContactNotesForm,
    ContactPhonesForm,
    ContactAddressesForm,
    ContactSelectForm,
    ContactFormCustomFields,
    ContactFormSeparator,
    ContactEmployerForm,
    ContactCompanyForm,
};

export type { ContactSelectFormType };
